/**
 * Build-time application configuration — Plan-ShopifyIsolatedFrontend.md §3.1.
 *
 * Single source of truth for the variables that distinguish the
 * default (`app.askprodigy.ai`, Stripe-billed) build from the Shopify
 * variant (`shop.askprodigy.ai`, Shopify-Managed-Pricing). All values
 * come from `import.meta.env` and are baked in at build time —
 * `npm run build` (default mode) yields the Stripe bundle,
 * `npm run build:shopify` (Vite `--mode shopify` loads `.env.shopify`)
 * yields the Shopify bundle.
 *
 * Consumers MUST import from here rather than reading `import.meta.env`
 * directly so we never end up with two surfaces drifting on the same
 * flag. The only legitimate exception is bootstrap code that runs
 * BEFORE this module is parsed (e.g. `src/services/api.ts` reading
 * `VITE_API_URL` to set the axios baseURL).
 */
const rawPlans =
    (import.meta.env.VITE_PLANS as string | undefined) ??
    'trial,starter,pro,business,enterprise';

export const APP_CONFIG = {
    /**
     * Which billing provider this build is wired against.
     * - `stripe` (default) — the existing `app.askprodigy.ai` SPA,
     *   includes Stripe checkout / portal / overage / bundles UI and
     *   the Shopify data-connector UI is GATED OUT (replaced by a
     *   marketing CTA pointing at `shop.askprodigy.ai`).
     * - `shopify` — the `shop.askprodigy.ai` variant, Stripe UI gated
     *   out, Shopify data-connector UI shown, "Sign in with Shopify"
     *   eventually added (Phase 3).
     */
    billingProvider: ((import.meta.env.VITE_BILLING_PROVIDER as
        | string
        | undefined) ?? 'stripe') as 'stripe' | 'shopify',

    /**
     * Subset of `Plan` keys that this build is allowed to display.
     * The Stripe build keeps everything; the Shopify build narrows
     * to `starter,pro,business` per the locked Managed-Pricing
     * catalogue (no Trial / Enterprise on Shopify).
     */
    allowedPlans: rawPlans
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),

    /**
     * Brand handle baked into the build — used for listing copy,
     * SEO titles, etc. Defaults to `prodagi` (main) and is overridden
     * to `prodagi-shopify` in `.env.shopify`.
     */
    brand:
        (import.meta.env.VITE_BRAND as string | undefined) ?? 'prodagi',

    /**
     * Public canonical URL of the bundle, used for OAuth redirect
     * URLs and outbound links from the OTHER bundle (the Stripe
     * build links to the Shopify build's `publicUrl` from the
     * "Connect Shopify? Sign up at shop.askprodigy.ai" CTA).
     */
    publicUrl:
        (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
        'https://app.askprodigy.ai',

    /**
     * Shopify shop URL the Stripe build's "Connect Shopify" CTA links to.
     * Defaults to the prod shop; staging sets VITE_SHOP_URL to the staging
     * shop CloudFront URL so the CTA points at the right environment.
     */
    shopUrl:
        (import.meta.env.VITE_SHOP_URL as string | undefined) ??
        'https://shop.askprodigy.ai',

    /**
     * Public canonical URL of the Stripe-billed app build
     * (`app.askprodigy.ai`). Sibling of `shopUrl`; used by the invalid trial-link
     * screen's "Go to app sign-up" button (Plan-TrialLinkInvalidTokenAndEnvHosts).
     * Defaults to the prod app; staging sets VITE_APP_URL to the staging app
     * CloudFront URL so the button points at the right environment.
     */
    appUrl:
        (import.meta.env.VITE_APP_URL as string | undefined) ??
        'https://app.askprodigy.ai',

    /**
     * Article URL the post-login Live Avatar ad modal links to
     * (`LiveAvatarAdModal.tsx`). The user creates the article in the
     * admin portal and sets this value. Empty/undefined → the modal
     * never renders (feature off until configured).
     */
    liveAvatarArticleUrl:
        (import.meta.env.VITE_LIVE_AVATAR_ARTICLE_URL as
            | string
            | undefined) ?? '',

    /**
     * Article URL the AI Stylist surfaces link to (the AgentEditorModal
     * Stylist toggle's "Learn more" link and the /stylist marketing page).
     * The user creates the article in the admin portal and sets this value.
     * Empty/undefined → the "Learn more" links are not rendered (no broken
     * link until configured). Shopify build only.
     */
    stylistArticleUrl:
        (import.meta.env.VITE_STYLIST_ARTICLE_URL as
            | string
            | undefined) ?? '',

    /**
     * Article URL the Virtual Try-On surfaces link to (the AgentEditorModal
     * Virtual Try-On toggle's "Learn more" link, the /virtual-try-on
     * educational page, and the Setup Guide step). The user creates the
     * article in the admin portal and sets this value. Empty/undefined → the
     * "Learn more" links are not rendered (no broken link until configured).
     * Shopify build only. Mirrors `stylistArticleUrl`.
     */
    virtualTryonArticleUrl:
        (import.meta.env.VITE_VIRTUAL_TRYON_ARTICLE_URL as
            | string
            | undefined) ?? '',

    /**
     * Master launch flag for Virtual Try-On (shop build only). Default off —
     * VTO surfaces stay hidden until VITE_VIRTUAL_TRYON_ENABLED=true. Layered
     * ON TOP of the IS_SHOPIFY_BUILD gate, so it never affects the app build.
     * Enabled ONLY when the string equals "true"; absent/empty/anything else
     * → hidden. The feature ships dormant per deployment environment — this is
     * the deployment kill-switch.
     */
    virtualTryonEnabled: import.meta.env.VITE_VIRTUAL_TRYON_ENABLED === 'true',
};

/**
 * Build-time staging flag — set via `VITE_APP_ENV=staging` in the staging
 * env files (`.env.dev.cloudfront`, `.env.shopify.dev`). Unset in prod
 * envs so the flag is falsy. Used to render the amber "STAGING" subtitle
 * under the sidebar logo in Layout.tsx.
 */
export const IS_STAGING =
    (import.meta.env.VITE_APP_ENV as string | undefined) === 'staging';

/**
 * Domain suffix used to construct preview agent email addresses in the
 * onboarding modal before the backend persists the real address. Mirrors
 * backend `settings.AGENT_EMAIL_DOMAIN` (Plan-AgentEmailSubdomainSplit.md
 * §4.4). Defaults to `agents.prodagi.ai`; the source of truth is still
 * `agent.email` from the API when present.
 */
export const AGENT_EMAIL_DOMAIN =
    (import.meta.env.VITE_AGENT_EMAIL_DOMAIN as string | undefined) ??
    'agents.prodagi.ai';
